/**
 * Loads the edition bundle and derives everything the UI needs from it.
 *
 * The bundle carries ONE time field per act — the instant — plus the
 * edition's IANA zone. Day buckets, labels and wall-clock times are computed
 * here, once, through @myrave/shared. The server never ships them, so they
 * cannot drift.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Empty, ErrorState } from '@/ui';
import { RaveLoading } from './RaveLoading';
import { backdrop } from './backdrop';
import { ApiError } from '@/lib/api';
import { signedInHere } from '@/lib/signOut';
import { askToSignIn } from '@/lib/signInFirst';
import { PaidRave, type PaywallDetails } from '@/visitor/Paywall';
import { AttendFirst } from '@/visitor/AttendFirst';
import { useAuth } from './AuthProvider';
import { useTenant } from './TenantProvider';
import { OfflineProvider } from '@/offline/OfflineProvider';
import { fetchPreviewBundle, refreshBundle, NoStoredCopyError, type RefreshResult } from '@/offline/bundle';
import { overForOffline } from '@/offline/pastRaves';
import { applyPreview, inPreview } from '@/lib/preview';
import { useEditionManifest } from './useManifest';
import type { Act, EditionBundle, Stage, Track } from '@myrave/shared';
import { applyStageDelays, dayBucket, dayLabel, timeLabel, wallClock, localized, localizeBundle, inkOn } from '@myrave/shared';
import { activityById } from '@myrave/shared';
import { flushMyDelays, readMyDelays, subscribeMyDelays } from '@/lib/stageDelays';
import { useLocale } from './LocaleProvider';
import { useRaveBios, withRaveBios } from '@/visitor/raveBios';
import { useT } from '@/i18n';
import { setCurrentRave } from '@/lib/currentRave';
import { ToApp } from './ToApp';
import { ReadOnly } from '@/visitor/ReadOnly';

/** `#rgb` or `#rrggbb` — what the API accepts, and nothing that could carry more CSS. */
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Paint the edition's accent pair onto the document root.
 *
 * On the ROOT and nowhere lower, because every accent tint in `ui/tokens.css`
 * is a `:root` custom property mixed from `var(--accent)` — it resolves where
 * it is declared, so an accent set on a descendant would change the solid
 * colour and leave every wash, ring and border OZORA orange.
 *
 * Removed on unmount and on an edition change, so a platform page (no
 * edition) and the next festival fall back to the token defaults instead of
 * inheriting the last festival's colours. `themeColor` is NOT the accent — it
 * is the browser bar, and `useEditionManifest` owns it.
 *
 * `--on-accent` (the ink on a selected pill, a primary button) goes WITH the
 * accent: dark or light, whichever reads on it (`inkOn`, `@myrave/shared`).
 * A fixed near-black ink was unreadable on any dark accent. Print overrides
 * it with `!important`, as it does the accent.
 */
export function useEditionAccent(accent?: string, accent2?: string): void {
  useEffect(() => {
    const root = document.documentElement.style;
    const set: string[] = [];
    const paint = (name: string, value: string) => { root.setProperty(name, value); set.push(name); };
    if (accent && HEX.test(accent)) {
      paint('--accent', accent);
      paint('--on-accent', inkOn(accent));
    }
    if (accent2 && HEX.test(accent2)) paint('--accent2', accent2);
    return () => { for (const name of set) root.removeProperty(name); };
  }, [accent, accent2]);
}

/** An act with its derived display values, computed once per bundle. */
export interface DisplayAct extends Act {
  startAtDate: Date;
  endAtDate: Date;
  /** The day it belongs to — the day it STARTS, even at 00:00. */
  day: string;
  timeLabel: string;
  startLabel: string;
  /**
   * Minutes a stage running late has pushed this set (the user, 2026-10-08):
   * the start/end above are already the shifted ones; this is the "+30" to
   * show beside them. Absent when on time.
   */
  delayMin?: number;
  /**
   * The name in the reader's language, falling back to the original.
   *
   * Resolved HERE, once per bundle, rather than at each of the dozen places an
   * act name is rendered. A screen that forgets to call `localized` is a
   * screen that is silently English in a Hebrew app, and that is not a bug
   * anyone notices in review.
   */
  displayName: string;
  /**
   * Who leads a session — its facilitator's name, in the reader's language
   * (2026-10-10). A session's NAME is the session ("Yin Yoga"); the person is
   * no longer part of it, so a screen that showed only the name would lose
   * them. Absent for a music set: the artist IS its name.
   */
  leader?: string;
  /** What a session is — "Yoga", "Talk" (`@myrave/shared/activity`), untranslated: render through `tr`. */
  activityLabel?: string;
  /** Its activity's emoji (`ACTIVITY_ICONS`) — shown where a session has no picture. */
  activityIcon?: string;
}

interface EditionState {
  bundle: EditionBundle;
  acts: DisplayAct[];
  actsBySlug: Map<string, DisplayAct>;
  stages: Stage[];
  stagesById: Map<string, Stage>;
  tracks: Track[];
  days: { day: string; label: string; weekday: string; dayNum: string }[];
  timezone: string;
  /**
   * Where the bundle on screen came from. `'offline'` means the request for a
   * newer one FAILED (or gave no answer in 2.5s) and the copy stored on this
   * device is being shown — a fact about this load, not a guess from
   * `navigator.onLine` or a probe race. `'stored'` is the stored copy while
   * that request is still out.
   */
  source: RefreshResult['source'];
  /** When that stored copy last came from the server, epoch ms. */
  storedAt?: number;
}

const Ctx = createContext<EditionState | null>(null);

export function useEdition(): EditionState {
  const value = useContext(Ctx);
  if (!value) throw new Error('useEdition called outside an EditionProvider');
  return value;
}

export function EditionProvider({ editionId, children }: { editionId: string; children: ReactNode }) {
  const tr = useT();
  // The paywall's name: a 402 carries no bundle, so `data` has none to give.
  const { tenant } = useTenant();
  // Read-through: the stored copy answers immediately, the network is
  // consulted with its version as an ETag. Offline, the stored copy IS the
  // answer — a festival app that shows nothing without a signal is useless
  // exactly where it is needed.
  //
  // Stored FIRST: a copy on this device is on screen at once, and the
  // revalidation (ETag, raced at 2.5s) replaces it here when it lands — a
  // hanging network never holds up a lineup this phone already has.
  const queryClient = useQueryClient();
  // A public page's framed preview reads the open sample bundle and stores nothing (`lib/preview.ts`).
  const [preview] = useState(inPreview);
  const previewSlug = tenant?.editions?.find((e) => e.id === editionId)?.slug ?? tenant?.editionSlug ?? null;
  const { data: result, isLoading, error, refetch } = useQuery({
    queryKey: preview ? ['bundle', editionId, 'preview'] : ['bundle', editionId],
    queryFn: () => preview ? fetchPreviewBundle(previewSlug) : refreshBundle(editionId, {
      onUpdate: (next) => { queryClient.setQueryData<RefreshResult>(['bundle', editionId], next); },
      // Refused behind a stored copy (402, 401…): load again, network first, so the paywall shows.
      onRefused: () => { void queryClient.invalidateQueries({ queryKey: ['bundle', editionId] }); },
    }),
    staleTime: 10 * 60_000,
  });
  const { locale, language } = useLocale();
  // The rave's bios in the reader's language, the phone's copy offline (step 76.10): laid over the line-up
  // as each performer's translation, so every screen and the stored copy read it alike.
  const bios = useRaveBios(editionId, language, !preview);
  const data = useMemo(() => (result?.bundle ? withRaveBios(result.bundle, bios, language) : undefined), [result?.bundle, bios, language]);

  // The shell above this provider (its footer's version matrix) cannot
  // `useEdition()`; tell it which rave is open, and that none is when it closes.
  const raveName = data?.edition.name;
  useEffect(() => {
    if (!raveName) return undefined;
    setCurrentRave({ editionId, name: raveName });
    return () => setCurrentRave(null);
  }, [editionId, raveName]);

  /*
   * Stage delays (2026-10-08): the rave's (in the bundle) and this phone's own
   * reports — its own only where the rave has none from the same set, so a
   * delay is never counted twice. Every screen reads the shifted times.
   */
  const myDelays = useSyncExternalStore(
    useCallback((cb) => subscribeMyDelays(editionId, cb), [editionId]),
    () => readMyDelays(editionId),
  );
  useEffect(() => {
    if (preview) return undefined;
    void flushMyDelays(editionId);
    const online = () => { void flushMyDelays(editionId); };
    window.addEventListener('online', online);
    return () => window.removeEventListener('online', online);
  }, [editionId, preview]);

  const value = useMemo<EditionState | null>(() => {
    if (!data) return null;
    /*
     * The rave's own words in the reader's language, once (step 73.10): its
     * name, categories, stages, map places and sponsor tiers — every screen
     * reads them from here, so none can print the English by forgetting.
     */
    const view = localizeBundle(data, locale);
    const tz = view.edition.timezone;

    const ravesDelays = data.delays ?? [];
    const delays = [...ravesDelays, ...myDelays.filter((d) => !ravesDelays.some((r) => r.fromActId === d.fromActId))];
    const shifted = applyStageDelays(data.acts, delays);

    const performers = new Map((data.artists ?? []).map((p) => [p.id, p]));
    const acts: DisplayAct[] = data.acts.map((a) => {
      const moved = shifted.get(a.id);
      const leader = a.facilitatorId ? performers.get(a.facilitatorId) : undefined;
      const activity = activityById(a.activity);
      const startAtDate = moved?.startAt ?? new Date(a.startAt);
      const endAtDate = moved?.endAt ?? new Date(a.endAt);
      return {
        ...a,
        startAtDate,
        endAtDate,
        // A set running late stays on the day it was given.
        day: dayBucket(new Date(a.startAt), tz),
        timeLabel: timeLabel(startAtDate, endAtDate, tz),
        startLabel: wallClock(startAtDate, tz),
        ...(moved ? { delayMin: Math.round(moved.delayMs / 60_000) } : {}),
        displayName: localized(a.name, a.nameLocal, locale),
        ...(leader ? { leader: localized(leader.name, leader.nameLocal, locale) } : {}),
        ...(activity ? { activityLabel: activity.label, activityIcon: activity.icon } : {}),
      };
    });

    /*
     * `label` is the one-line form ("Fri 24 Jul"); `weekday` and `dayNum` are
     * the two-line form the day pill actually renders.
     *
     * Eleven one-line pills at 390px show three and a half of them, so the
     * four days most people are choosing between are off-screen behind a
     * horizontal scroll nobody knows is there. Split over two lines they are
     * 56px wide and six fit. The pilot does it this way and this is why.
     */
    const days = [...new Set(acts.map((a) => a.day))]
      .sort()
      .map((day) => {
        const label = dayLabel(day, tz);
        const [wd = '', num = ''] = label.split(' ');
        return { day, label, weekday: wd, dayNum: num };
      });

    return {
      bundle: view,
      acts,
      actsBySlug: new Map(acts.map((a) => [a.slug, a])),
      stages: [...view.stages],
      stagesById: new Map(view.stages.map((s) => [s.id, s])),
      tracks: [...view.tracks],
      days,
      timezone: tz,
      source: result?.source ?? 'fresh',
      storedAt: result?.fetchedAt,
    };
  }, [data, result, locale, myDelays]);

  // A preview holds its sample picks and opens its artist before the app renders (`lib/preview.ts`).
  if (preview && value) {
    applyPreview(editionId, value.acts, (value.bundle as { previewFeaturedActId?: string | null }).previewFeaturedActId);
  }

  // Declared before the early returns so the hook order stays stable.
  useEditionManifest(data ? editionId : null, data?.edition.themeColor);
  useEditionAccent(data?.edition.accentColor, data?.edition.accent2Color);
  const { user, loading: authLoading } = useAuth();

  /*
   * The rave's own loading screen — its logo, dates and place under the
   * spinning mark, and the typed lines (`RaveLoading.tsx`; the user,
   * 2026-10-07). It was a grey skeleton of the schedule, which showed
   * nothing of the rave being opened.
   */
  // On the rave's own backdrop, from the cover `/tenant` named: its artwork is
  // on screen while the lineup loads, and stays put when it arrives (step 14).
  if (isLoading || authLoading) {
    const wait = <RaveLoading label={tr('Loading the lineup…')} tenant={tenant} />;
    return tenant?.coverUrl
      ? <div className="edition-backdrop" style={backdrop(tenant.coverUrl)}>{wait}</div>
      : wait;
  }

  /**
   * Pre-launch, the API refuses content to an anonymous caller (§61.7). That
   * is not an error to report — it is a request to sign in, and the app should
   * say so rather than showing "could not load the lineup" to someone whose
   * connection is fine.
   */
  // Since 2026-10-26 a rave reads signed out, so this is a PAID rave while purchases are on:
  // the sign-in page (providers and phone — no password sheet any more), and back here.
  if (error instanceof ApiError && error.status === 401 && !user) {
    return (
      <Empty
        title={tr('Sign in to MyRave')}
        hint={tr('Sign in, or create an account, to see the lineup.')}
        action={<Button onClick={() => askToSignIn()}>{tr('Sign in')}</Button>}
      />
    );
  }

  /**
   * A festival that has to be bought answers 402, and the refusal carries the
   * price.
   *
   * Checked BEFORE the generic branch below, which would otherwise say "check
   * your connection" over a perfectly good one — the message most likely to
   * make someone give up rather than pay.
   */
  // Not attending yet (the user, 2026-10-15): the rave, and Attend — never its lineup.
  if (error instanceof ApiError && error.status === 403 && error.code === 'attend_first') {
    return (
      <ReadOnly>
        <AttendFirst editionId={editionId} festivalName={data?.festival.name ?? tenant?.festivalName ?? tr('This rave')}
          onAttended={() => void refetch()} />
      </ReadOnly>
    );
  }

  if (error instanceof ApiError && error.status === 402) {
    const details = (error.details ?? {}) as PaywallDetails;
    return (
      /* Buy, redeem and code are inert in the wizard's read-only preview. */
      <ReadOnly>
        {/* The paywall only while payments are on; else "not open right now" (2026-10-29). */}
        <PaidRave
          festivalName={data?.festival.name ?? tenant?.festivalName ?? tr('This rave')}
          details={{ ...details, editionId: details.editionId ?? editionId }}
          // Ask for the bundle again; the purchase granted access server-side.
          onPurchased={() => void refetch()}
        />
      </ReadOnly>
    );
  }

  /*
   * No signal AND nothing stored: the one state a festival app cannot cover.
   *
   * Said as what it is — this phone has never had the lineup — with the one
   * thing that fixes it. "Try again later" would send somebody back into a
   * field with no reception to wait for something that is not coming.
   */
  // The server's definitive "no": a draft, a private rave, a deleted one. Not a
  // screen — the main app (the user, 2026-10-06). A network failure never gets
  // here: that is `NoStoredCopyError` below, or the stored copy on screen.
  if (error instanceof ApiError && error.status === 404) return <ToApp />;

  // Signed out nothing is stored (online only, 2026-10-27), so "find a signal once … stored on this
  // phone" would be a promise the app does not keep: say what it is (the user: "say you are offline").
  if (error instanceof NoStoredCopyError && !signedInHere()) {
    return (
      <ErrorState
        title={tr('You are offline')}
        detail={tr('Connect to the internet to see the raves.')}
        retry={() => void refetch()}
        retryLabel={tr('Try again')}
        retryVariant="primary"
      />
    );
  }

  // A rave over by more than two days keeps nothing on the phone: past raves open with a connection (2026-11-02).
  const thisEdition = tenant?.editions?.find((e) => e.id === editionId);
  if (error instanceof NoStoredCopyError && overForOffline(thisEdition?.endsAt)) {
    return (
      <ErrorState
        title={tr('This rave is over')}
        detail={tr('Past raves open with an internet connection — they are not kept on the phone.')}
        retry={() => void refetch()}
        retryLabel={tr('Try again')}
        retryVariant="primary"
      />
    );
  }

  if (error instanceof NoStoredCopyError) {
    return (
      <ErrorState
        title={tr('Schedule unavailable')}
        detail={tr('There is no connection, and the lineup has not been saved to this phone yet.')}
        tip={(
          <>
            <strong className="app-strong">{tr('Find a signal once.')}</strong>
            {' '}{tr('As soon as the lineup has loaded it is stored on this phone, and from then on it works with no signal at all.')}
          </>
        )}
        retry={() => void refetch()}
        retryLabel={tr('Try reloading')}
        retryVariant="primary"
      />
    );
  }

  if (error || !value) {
    /*
     * `ErrorState`, not `Empty`. An empty state says "there is nothing here",
     * which is a different fact from "we could not find out" — and at a
     * festival the difference is whether someone goes looking for a stage.
     */
    return (
      <ErrorState
        title={tr('The lineup got lost in the crowd')}
        detail={tr('Give it another go. If there is no signal out here, it will find its way back on its own — and anything you already downloaded is still yours.')}
        retry={() => void refetch()}
      />
    );
  }
  return (
    <Ctx.Provider value={value}>
      <OfflineProvider bundle={value.bundle}>{children}</OfflineProvider>
    </Ctx.Provider>
  );
}
